import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import sericesApi from "../../../api/service/service.api";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import ProcessingBar2 from "../../../components/ProcessingBar/ProcessingBar2";

import { Listbox } from "@headlessui/react";
import { ArrowLeft } from "lucide-react";

const BasicDetails = ({ initialData = null, isEdit = false, serviceId }) => {
  console.log({ initialData });
  const [serviceCategory, setServiceCategory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();

  const operator = user;

  const getServiceCategories = async () => {
    try {
      const operatorId = operator?._id;
      const response = await sericesApi.getServiceCategory({
        operator: operatorId,
      });
      if (response.success && response.category) {
        setServiceCategory(response.category);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (!operator?._id) return;
    setIsLoading(true);
    const loadOperatorData = async () => {
      try {
        await Promise.all([getServiceCategories()]);
      } catch (err) {
        console.error("Error loading operator data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadOperatorData();
  }, [operator]);

  // console.log({ serviceCategory });

  // ✅ Yup schema
  const ServiceSchema = Yup.object().shape({
    serviceName: Yup.string().required("Service name is required").max(255),
    description: Yup.string().max(1000),
    category: Yup.object().nullable().required("Select a category"),
    // priceType: Yup.string().required("Select price type"),
    price: Yup.number().required("Enter a price"),
    duration: Yup.string().required("Select duration"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        title: values.serviceName,
        desc: values.duration,
        description: values.description,
        price: values.price.toString(),
        category: values.category._id,
        operator: user._id,
        image: "",
      };
      let res;
      if (isEdit) {
        res = await sericesApi.updateService(serviceId, payload);
      } else {
        res = await sericesApi.createService(payload);
      }

      if (res.success) {
        console.log("Service created:", res.service);
        navigate("/bookingservices");
      }
    } catch (err) {
      console.error("Error creating service:", err);
      alert(err.message || "Failed to create service");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          serviceName: initialData?.serviceName || "",
          description: initialData?.description || "",
          category:
            serviceCategory.find((c) => c._id === initialData?.category) ||
            null,
          // priceType: initialData?.priceType || "",
          price: initialData?.price || "",
          duration: initialData?.duration || "",
        }}
        validationSchema={ServiceSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="space-y-10 ">
            {/* Basic details */}
            <section>
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold mb-6">Basic details</h2>
                {/* Submit button */}
                <div className="flex justify-end pt-6 hidden md:flex gap-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 rounded-md bg-redlight text-white hover:bg-reddark disabled:opacity-50"
                  >
                    {isSubmitting ? <ProcessingBar2 /> : "Save Service"}
                  </button>
                </div>
              </div>

              {/* Service Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text">
                  Service name{" "}
                  <span className="text-textGray text-xs">
                    ({values.serviceName.length}/255)
                  </span>
                </label>
                <Field
                  name="serviceName"
                  type="text"
                  placeholder="Add a service name"
                  className="mt-1 block w-full border border-border bg-dry rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-redlight"
                />
                <ErrorMessage
                  name="serviceName"
                  component="p"
                  className="text-xs text-red-500"
                />
              </div>

              {/* Type + Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-textGray">
                    Service type
                  </label>
                  <Field
                    as="select"
                    name="serviceType"
                    className="mt-1 block w-full  rounded-md px-3 py-2 text-sm bg-dry border border-border"
                  >
                    <option>Select service type</option>
                    <option value="Nail">Nail</option>
                    <option value="Beauty">Beauty</option>
                  </Field>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textGray">
                    Category
                  </label>
                  <Listbox
                    value={values.category}
                    onChange={(val) => setFieldValue("category", val)}
                  >
                    <Listbox.Button className="mt-1 block w-full border border-border rounded-md px-3 py-2 text-left text-sm ">
                      {values.category ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: values.category.color }}
                          />
                          {values.category.name}
                        </div>
                      ) : (
                        "Select category"
                      )}
                    </Listbox.Button>

                    <Listbox.Options className="absolute mt-1 w-[370px] lg:w-[500px]  border bg-dry border-border rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col z-10">
                      <div className="overflow-y-auto max-h-48 scrollbar-thin">
                        {serviceCategory.map((cat) => (
                          <Listbox.Option
                            key={cat._id}
                            value={cat}
                            className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-redlightlight hover:text-dry"
                          >
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: cat.color }}
                            />
                            {cat.name}
                          </Listbox.Option>
                        ))}
                      </div>

                      {/* fixed bottom */}
                      <div className="border-t border-border">
                        <div className="px-3 py-2 text-redlight cursor-pointer hover:bg-gray-100">
                          + Add category
                        </div>
                      </div>
                    </Listbox.Options>
                  </Listbox>
                  <ErrorMessage
                    name="category"
                    component="p"
                    className="text-xs text-red-500"
                  />

                  <p className="text-xs text-gray-400 mt-1">
                    The category displayed to you, and to clients online
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-textGray">
                  Description (Optional)
                </label>
                <Field
                  as="textarea"
                  name="description"
                  placeholder="Add a short description"
                  rows={3}
                  className="mt-1 block w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-redlight bg-dry"
                />
                <ErrorMessage
                  name="description"
                  component="p"
                  className="text-xs text-red-500"
                />
                <p className="text-xs text-gray-400 text-right">
                  {values.description.length}/1000
                </p>
              </div>
            </section>

            {/* Pricing and duration */}
            <section>
              <h2 className="text-xl font-semibold mb-6">
                Pricing and duration
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                {/* Price type */}
                <div>
                  <label className="block text-sm font-medium text-textGray">
                    Price type
                  </label>
                  <Listbox
                    value={values.priceType}
                    onChange={(val) => setFieldValue("priceType", val)}
                  >
                    <Listbox.Button className="mt-1 block w-full border border-border rounded-md px-3 py-2 text-left text-sm ">
                      {values.priceType ? (
                        <div className="flex items-center gap-2">
                          {values.priceType}
                        </div>
                      ) : (
                        "-"
                      )}
                    </Listbox.Button>

                    <Listbox.Options className="absolute mt-1 w-[370px] lg:w-[370px] border bg-dry border-border rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col z-10">
                      <div className="overflow-y-auto max-h-48 scrollbar-thin">
                        {["Fixed", "From", "Range"].map((pricetype) => (
                          <Listbox.Option
                            key={pricetype}
                            value={pricetype}
                            className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-redlightlight hover:text-dry"
                          >
                            {pricetype}
                          </Listbox.Option>
                        ))}
                      </div>
                    </Listbox.Options>
                  </Listbox>
                  <ErrorMessage
                    name="priceType"
                    component="p"
                    className="text-xs text-red-500"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-textGray">
                    Price
                  </label>
                  <Field
                    name="price"
                    type="number"
                    placeholder="NZ$ 0.00"
                    className="mt-1 block w-full border border-border bg-dry rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-redlight"
                  />
                  <ErrorMessage
                    name="price"
                    component="p"
                    className="text-xs text-red-500"
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-textGray">
                    Duration
                  </label>
                  <Listbox
                    value={values.duration}
                    onChange={(val) => setFieldValue("duration", val)}
                  >
                    <Listbox.Button className="mt-1 block w-full border border-border rounded-md px-3 py-2 text-left text-sm ">
                      {values.duration ? (
                        <div className="flex items-center gap-2">
                          {values.duration}
                        </div>
                      ) : (
                        "-"
                      )}
                    </Listbox.Button>

                    <Listbox.Options className="absolute mt-1 w-[370px] lg:w-[370px]  border bg-dry border-border rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col z-10">
                      <div className="overflow-y-auto max-h-48 scrollbar-thin">
                        {[
                          "15 mins",
                          "30 mins",
                          "45 mins",
                          "1 hr",
                          "1 hr 15 mins",
                          "1 hr 30 mins",
                          "1 hr 45 mins",
                          "2 hr",
                        ].map((dur) => (
                          <Listbox.Option
                            key={dur}
                            value={dur}
                            className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-redlightlight hover:text-dry"
                          >
                            {dur}
                          </Listbox.Option>
                        ))}
                      </div>
                    </Listbox.Options>
                  </Listbox>
                  <ErrorMessage
                    name="duration"
                    component="p"
                    className="text-xs text-red-500"
                  />
                </div>
              </div>

              {/* Extra time + Options */}
              <div className="flex gap-4 mt-4 mb-40">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm px-3 py-2 border border-border rounded-md"
                >
                  + Add extra time
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-2 border border-border rounded-md"
                >
                  Options
                </button>
              </div>
            </section>

            {/* Mobile Buttons */}

            <div className="md:hidden fixed bottom-0 left-0 right-0 border-t bg-dry p-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2 rounded-md bg-redlight text-white hover:bg-reddark disabled:opacity-50"
              >
                {isSubmitting ? <ProcessingBar2 /> : "Save Service"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default BasicDetails;
